import React from 'react';
import {
  Checkbox, CheckPicker, Stack,
} from 'rsuite';
import { useStoreActions, useStoreState } from '../../store/hooks';
import ApiGroupIcon from '../../utils/apiGroup';
import { ApiGroup } from '../../api/model/ApiGroup';

type ApiGroupCheckState = {
  marketplaceCodes: string[];
  isChecked: boolean;
  isIndeterminate: boolean;
};

function getMarketplaceCodes(
  eshops: { apiGroup: string; code: string }[],
  group: string,
): string[] {
  return eshops
    .filter((eshop) => eshop.apiGroup === group)
    .map((eshop) => eshop.code);
}

function getApiGroupCheckState(
  eshops: { apiGroup: string; code: string }[],
  apiGroup: string,
  selectedMarketplaceCodes: string[],
): ApiGroupCheckState {
  const marketplaceCodes = getMarketplaceCodes(eshops, apiGroup);
  const isChecked = marketplaceCodes.length > 0 && marketplaceCodes
    .every((code) => selectedMarketplaceCodes.includes(code));
  const isIndeterminate = !isChecked && marketplaceCodes
    .some((code) => selectedMarketplaceCodes.includes(code));

  return { marketplaceCodes, isChecked, isIndeterminate };
}

function handleGroupClick(
  apiGroup: string,
  checked: boolean,
  eshops: { apiGroup: string; code: string }[],
  selectedMarketplaceCodes: string[],
  setMarketplaceCodes: (codes: string[]) => void,
) {
  const marketplaceCodes = getMarketplaceCodes(eshops, apiGroup);
  let newSelected = [...selectedMarketplaceCodes];

  if (checked) {
    marketplaceCodes.forEach((code) => {
      if (!newSelected.includes(code)) {
        newSelected.push(code);
      }
    });
  } else {
    newSelected = newSelected.filter((code) => !marketplaceCodes.includes(code));
  }

  setMarketplaceCodes(newSelected);
}

function handleSelectAll(
  checked: boolean,
  eshops: { apiGroup: string; code: string }[],
  setMarketplaceCodes: (codes: string[]) => void,
) {
  setMarketplaceCodes(checked ? eshops.map((eshop) => eshop.code) : []);
}

function onMarketplaceChange(
  value: string[] | null,
  setMarketplaceCodes: (codes: string[]) => void,
) {
  setMarketplaceCodes(value || []);
}

interface MarketplacePickerProps {
  pickerData: { label: string; value: string; groupBy: string }[];
  selectedMarketplaceCodes: string[];
  setMarketplaceCodes: (codes: string[]) => void;
  allChecked: boolean;
  allIndeterminate: boolean;
  eshops: { apiGroup: string; code: string }[];
}

function MarketplacePicker({
  pickerData,
  selectedMarketplaceCodes,
  setMarketplaceCodes,
  allChecked,
  allIndeterminate,
  eshops,
}: Readonly<MarketplacePickerProps>) {
  return (
    <Stack spacing={10} alignItems="flex-start">
      <CheckPicker
        style={{ width: 300 }}
        placeholder="Select Marketplaces"
        data={pickerData}
        value={selectedMarketplaceCodes}
        onChange={(value) => onMarketplaceChange(value, setMarketplaceCodes)}
        groupBy="groupBy"
        renderMenu={(menu) => (
          <div>
            <div style={{ borderBottom: '1px solid #e5e5e5' }}>
              <Checkbox
                checked={allChecked}
                indeterminate={allIndeterminate}
                onChange={(_ignored, checked) => handleSelectAll(
                  checked,
                  eshops,
                  setMarketplaceCodes,
                )}
              >
                Select All
              </Checkbox>
            </div>
            {menu}
          </div>
        )}
        renderMenuItem={(label) => (
          <div style={{ paddingLeft: 10 }}>
            {label}
          </div>
        )}
        renderMenuGroup={(title) => {
          const { isChecked, isIndeterminate } = getApiGroupCheckState(
            eshops,
            title as string,
            selectedMarketplaceCodes,
          );

          return (
            <Stack spacing={10}>
              <Checkbox
                checked={isChecked}
                indeterminate={isIndeterminate}
                onChange={(_ignored, checked) => handleGroupClick(
                  title as string,
                  checked,
                  eshops,
                  selectedMarketplaceCodes,
                  setMarketplaceCodes,
                )}
                onClick={(event) => event.stopPropagation()}
              />
              <ApiGroupIcon apiGroup={title as ApiGroup} />
              {title}
            </Stack>
          );
        }}
        cleanable={false}
      />
    </Stack>
  );
}

export default function MarketplaceSelector() {
  const { setMarketplaceCodes } = useStoreActions((actions) => actions.pricingData);
  const currentFilter = useStoreState((state) => state.pricingData.currentFilter);
  const eshops = useStoreState((state) => state.catalogs.eshops);

  const selectedMarketplaceCodes = currentFilter.filter.marketplaceCodes || [];

  const allChecked = eshops.length > 0 && selectedMarketplaceCodes.length === eshops.length;
  const allIndeterminate = !allChecked && selectedMarketplaceCodes.length > 0;

  const pickerData = eshops.map((eshop) => ({
    label: eshop.code,
    value: eshop.code,
    groupBy: eshop.apiGroup,
  }));

  return (
    <MarketplacePicker
      pickerData={pickerData}
      selectedMarketplaceCodes={selectedMarketplaceCodes}
      setMarketplaceCodes={setMarketplaceCodes}
      allChecked={allChecked}
      allIndeterminate={allIndeterminate}
      eshops={eshops}
    />
  );
}
